import type { ServiceResponse } from './types';
import type { User, Order } from '../types/User';
import type { Product } from '../types/Product';
import type { Address } from '../contexts/AddressContext';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

// Backend response types
interface CustomerResponse {
  _id: string;
  id: string;
  name: string;
  email?: string;
  phone: string;
  phoneVerified: boolean;
  customerTier?: 'bronze' | 'silver' | 'gold' | 'platinum';
  totalSpent?: number;
  totalOrders?: number;
  wishlist?: string[];
  shippingAddresses?: AddressResponse[]; // Backend returns shippingAddresses, not addresses
  createdAt: string;
  updatedAt: string;
}

interface AddressResponse {
  _id?: string;
  id?: string;
  label: string; // Backend uses 'label' not 'type'
  street: string; // Backend uses 'street' not 'address'
  city: string;
  state: string;
  zipCode: string;
  country: string;
  isDefault: boolean;
  // Frontend-specific fields that aren't in backend
  name?: string;
  phone?: string;
}

interface OrderResponse {
  _id: string;
  id: string;
  orderNumber: string;
  customerId: string;
  items: Array<{
    productId: string;
    productName: string;
    productImage?: string;
    price: number;
    quantity: number;
  }>;
  subtotal: number;
  total: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  shippingAddress: AddressResponse;
  billingAddress?: AddressResponse;
  paymentMethod: 'cod' | 'upi' | 'card' | 'netbanking';
  paymentStatus: 'pending' | 'paid' | 'failed';
  paymentScreenshot?: string;
  trackingNumber?: string;
  estimatedDelivery?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

class CustomerService {
  // Request deduplication
  private pendingRequests: Map<string, Promise<any>> = new Map();

  // Transform backend customer to frontend user format
  private transformCustomer(customer: CustomerResponse): User {
    return {
      id: customer._id || customer.id,
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone,
      joinedDate: customer.createdAt,
      tier: customer.customerTier,
      totalSpent: customer.totalSpent || 0,
      totalOrders: customer.totalOrders || 0
    };
  }

  // Transform backend order to frontend format
  private transformOrder(order: OrderResponse): Order {
    return {
      id: order._id || order.id,
      userId: order.customerId,
      orderNumber: order.orderNumber,
      items: order.items.map(item => ({
        productId: item.productId,
        productName: item.productName,
        productImage: item.productImage || '',
        price: item.price,
        quantity: item.quantity
      })),
      subtotal: order.subtotal,
      total: order.total,
      status: order.status,
      createdAt: order.createdAt,
      updatedAt: order.updatedAt,
      shippingAddress: {
        name: order.shippingAddress.name || '',
        address: order.shippingAddress.street || '',
        city: order.shippingAddress.city,
        state: order.shippingAddress.state,
        zipCode: order.shippingAddress.zipCode,
        phone: order.shippingAddress.phone || ''
      },
      trackingNumber: order.trackingNumber,
      estimatedDelivery: order.estimatedDelivery,
      paymentMethod: order.paymentMethod === 'card' || order.paymentMethod === 'netbanking' ? 'upi' : order.paymentMethod,
      paymentScreenshot: order.paymentScreenshot
    };
  }

  // Transform backend address to frontend format
  private transformAddress(address: AddressResponse): Address {
    return {
      id: address._id || address.id || Date.now().toString(),
      label: address.label,
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
      isDefault: address.isDefault
    };
  }

  // Get current customer details
  async getCurrentCustomer(): Promise<ServiceResponse<User | null>> {
    const cacheKey = 'getCurrentCustomer';
    
    // Check if there's already a pending request
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        const customer = await apiRequest<CustomerResponse>(
          API_ENDPOINTS.AUTH.ME,
          { method: 'GET' },
          true
        );

        return {
          success: true,
          data: this.transformCustomer(customer)
        };
      } catch (error) {
        console.error('Error fetching current customer:', error);
        return {
          success: false,
          data: null,
          error: error instanceof Error ? error.message : 'Failed to fetch customer details'
        };
      } finally {
        // Clean up pending request
        this.pendingRequests.delete(cacheKey);
      }
    })();

    // Store the pending request
    this.pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  // Update customer details
  async updateCustomer(_customerId: string, data: Partial<User>): Promise<ServiceResponse<User | null>> {
    try {
      const updateData: any = {};
      if (data.name !== undefined) updateData.name = data.name;
      if (data.email !== undefined) updateData.email = data.email;

      const customer = await apiRequest<CustomerResponse>(
        API_ENDPOINTS.CUSTOMERS.UPDATE_ME,
        {
          method: 'PUT',
          body: JSON.stringify(updateData)
        },
        true
      );

      return {
        success: true,
        data: this.transformCustomer(customer)
      };
    } catch (error) {
      console.error('Error updating customer:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update customer details'
      };
    }
  }

  // Get customer orders
  async getCustomerOrders(_customerId: string): Promise<ServiceResponse<Order[]>> {
    try {
      const orders = await apiRequest<OrderResponse[]>(
        API_ENDPOINTS.ORDERS.MY_ORDERS,
        { method: 'GET' },
        true
      );

      const transformedOrders = orders
        .map(order => this.transformOrder(order))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      return {
        success: true,
        data: transformedOrders
      };
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch orders'
      };
    }
  }

  // Get customer wishlist
  async getWishlist(_customerId: string): Promise<ServiceResponse<Product[]>> {
    try {
      // Get current customer data which includes wishlist product IDs
      await apiRequest<CustomerResponse>(
        API_ENDPOINTS.AUTH.ME,
        { method: 'GET' },
        true
      );
      
      // For now, return empty array as we need to fetch product details separately
      // TODO: Implement fetching product details for wishlist items
      const wishlist: Product[] = [];

      return {
        success: true,
        data: wishlist || []
      };
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch wishlist'
      };
    }
  }

  // Add to wishlist
  async addToWishlist(_customerId: string, productId: string): Promise<ServiceResponse<boolean>> {
    try {
      await apiRequest<void>(
        API_ENDPOINTS.CUSTOMERS.MY_WISHLIST,
        {
          method: 'PUT',
          body: JSON.stringify({ productId, action: 'add' })
        },
        true
      );

      return {
        success: true,
        data: true
      };
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return {
        success: false,
        data: false,
        error: error instanceof Error ? error.message : 'Failed to add to wishlist'
      };
    }
  }

  // Remove from wishlist
  async removeFromWishlist(_customerId: string, productId: string): Promise<ServiceResponse<boolean>> {
    try {
      await apiRequest<void>(
        API_ENDPOINTS.CUSTOMERS.MY_WISHLIST,
        {
          method: 'PUT',
          body: JSON.stringify({ productId, action: 'remove' })
        },
        true
      );

      return {
        success: true,
        data: true
      };
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return {
        success: false,
        data: false,
        error: error instanceof Error ? error.message : 'Failed to remove from wishlist'
      };
    }
  }

  // Get customer addresses
  async getAddresses(_customerId: string): Promise<ServiceResponse<Address[]>> {
    const cacheKey = `getAddresses`; // No need for customerId in cache key for JWT-based endpoint
    
    // Check if there's already a pending request for this customer's addresses
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        // Fetch current customer data which includes addresses
        const customer = await apiRequest<CustomerResponse>(
          API_ENDPOINTS.AUTH.ME,
          { method: 'GET' },
          true
        );
        
        const addresses = customer.shippingAddresses || [];

        const transformedAddresses = addresses.map(addr => this.transformAddress(addr));

        return {
          success: true,
          data: transformedAddresses
        };
      } catch (error) {
        console.error('Error fetching addresses:', error);
        return {
          success: false,
          data: [],
          error: error instanceof Error ? error.message : 'Failed to fetch addresses'
        };
      } finally {
        // Clean up pending request
        this.pendingRequests.delete(cacheKey);
      }
    })();

    // Store the pending request
    this.pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  // Add address
  async addAddress(_customerId: string, address: Omit<Address, 'id'>): Promise<ServiceResponse<Address | null>> {
    try {
      // No transformation needed - frontend now uses backend field names
      const addressData = {
        label: address.label,
        street: address.street,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        country: address.country || 'India', // Default country if not provided
        isDefault: address.isDefault
      };

      const response = await apiRequest<CustomerResponse>(
        API_ENDPOINTS.CUSTOMERS.ADD_MY_ADDRESS,
        {
          method: 'POST',
          body: JSON.stringify(addressData)
        },
        true
      );

      // Get the newly added address from the updated customer data
      const addresses = response.shippingAddresses || [];
      const newAddress = addresses[addresses.length - 1];

      return {
        success: true,
        data: newAddress ? this.transformAddress(newAddress) : null
      };
    } catch (error) {
      console.error('Error adding address:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to add address'
      };
    }
  }

  // Update address
  async updateAddress(_customerId: string, addressId: string, address: Partial<Address>): Promise<ServiceResponse<Address | null>> {
    try {
      const updatedAddress = await apiRequest<AddressResponse>(
        API_ENDPOINTS.CUSTOMERS.UPDATE_ADDRESS(_customerId, addressId),
        {
          method: 'PUT',
          body: JSON.stringify(address)
        },
        true
      );

      return {
        success: true,
        data: this.transformAddress(updatedAddress)
      };
    } catch (error) {
      console.error('Error updating address:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update address'
      };
    }
  }

  // Delete address
  async deleteAddress(_customerId: string, addressId: string): Promise<ServiceResponse<boolean>> {
    try {
      await apiRequest<void>(
        API_ENDPOINTS.CUSTOMERS.DELETE_MY_ADDRESS(addressId),
        {
          method: 'DELETE'
        },
        true
      );

      return {
        success: true,
        data: true
      };
    } catch (error) {
      console.error('Error deleting address:', error);
      return {
        success: false,
        data: false,
        error: error instanceof Error ? error.message : 'Failed to delete address'
      };
    }
  }

  // Set default address
  async setDefaultAddress(_customerId: string, addressId: string): Promise<ServiceResponse<boolean>> {
    try {
      await apiRequest<void>(
        API_ENDPOINTS.CUSTOMERS.SET_DEFAULT_ADDRESS(_customerId, addressId),
        {
          method: 'PUT'
        },
        true
      );

      return {
        success: true,
        data: true
      };
    } catch (error) {
      console.error('Error setting default address:', error);
      return {
        success: false,
        data: false,
        error: error instanceof Error ? error.message : 'Failed to set default address'
      };
    }
  }
}

export const customerService = new CustomerService();